import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

import useHidePhi from '@/hooks/useHidePhi';
import { HIDE_PHI_SHORTCUT_LABEL } from '@/hooks/useHidePhiHotkey';
import usePhiBlurSource from '@/hooks/usePhiBlurSource';
import { Button, Tooltip } from '@/ui';

import './styles.css';
import type { Props } from './types';

const getTooltip = (hidePhi: boolean, precursorBlur: boolean) => {
  if (hidePhi) return `Show PHI (${HIDE_PHI_SHORTCUT_LABEL})`;
  if (precursorBlur) return 'PHI hidden for screenshot';
  return `Hide PHI (${HIDE_PHI_SHORTCUT_LABEL})`;
};

/**
 * One-click "Hide PHI" switch for the top bar. While it is on every `.mp-sensitive` element is
 * blurred, for screen shares and screenshot tools the keyboard precursor can't see coming.
 *
 * It also shows the hidden state while a screenshot chord is being held, so staff can see PHI was
 * blurred for the capture. `aria-pressed` still tracks only the manual toggle, which is what a
 * click changes.
 */
const HidePhiButton = ({ className }: Props) => {
  const { hidePhi, toggleHidePhi } = useHidePhi();
  const precursorBlur = usePhiBlurSource('precursor');
  const hidden = hidePhi || precursorBlur;

  return (
    <Tooltip placement="bottom" content={getTooltip(hidePhi, precursorBlur)}>
      <Button
        type="button"
        variant={hidden ? 'subtle' : 'ghost'}
        color={hidden ? 'warning' : 'neutral'}
        size="icon-xs"
        leftIcon={hidden ? faEyeSlash : faEye}
        data-phi-hidden={hidden}
        aria-label="Hide PHI"
        aria-pressed={hidePhi}
        aria-keyshortcuts="Control+Shift+H"
        className={className}
        onClick={toggleHidePhi}
      />
    </Tooltip>
  );
};

export default React.memo(HidePhiButton);
